import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  input,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import {animate, state, style, transition, trigger} from '@angular/animations';

import {SvgSpriteIcon} from '@selpia/shared/ui';
import {AiButtonComponent} from '@selpia/data-batch/ui';
import {SwiperContainer} from 'swiper/element';
import {DataBatchFacade} from '@selpia/data-batch/data-access';
import {OptionsFacade} from '@selpia/options/data-access';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {PerspectiveFacade} from '@selpia/perspective/data-access';
import {DateTools} from '@selpia/shared/utils';

import {InsightTagComponent} from '../insight-tag/insight-tag.component';
import {InsightButtonComponent} from '../insight-button/insight-button.component';
import {NgTemplateOutlet} from '@angular/common';
import {Insight} from '../models/insight.model';

@Component({
  selector: 'lib-feature-insights',
  templateUrl: './feature-insights.component.html',
  styleUrl: './feature-insights.component.scss',
  imports: [
    AiButtonComponent,
    InsightTagComponent,
    InsightButtonComponent,
    NgTemplateOutlet,
  ],
  host: {
    '[class.open]': 'showInsights()',
    '[class.extended]': 'showDetails()',
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [
    trigger('popIn', [
      state('collapsed', style({
        height: '152px',
        overflow: 'hidden',
        transform: 'translateY(0)',
      })),
      state('extended', style({
        height: '*',
        transform: 'translateY(0)',
      })),
      transition(':enter', [
        style({
          height: '152px',
          overflow: 'hidden',
          transform: 'translateY(100%)',
        }),
        animate("300ms cubic-bezier(0.243, 1.363, 0.492, 0.998)")]),
      transition('extended => void', [
        animate("300ms cubic-bezier(0.7, 0, 0.84, 0)",
          style({
            transform: 'translateY(100%)',
          }))]),
      transition('collapsed => void', [
        animate("200ms cubic-bezier(0.7, 0, 0.84, 0)",
          style({
            transform: 'translateY(100%)',
          }))]),
      transition('collapsed <=> extended', [
        animate('300ms ease-in-out'),
      ]),
    ]),
  ],
})
export class FeatureInsightsComponent {
  readonly #dataBatchFacade = inject(DataBatchFacade);
  readonly #optionsFacade = inject(OptionsFacade);
  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #perspectiveFacade = inject(PerspectiveFacade);

  readonly #url: string = S2WX_API_URL + '/DataBatch';

  protected readonly summarizationKey = computed(() => this.#dataBatchFacade.summarizationKey() || '');

  readonly #perspective = computed(() => this.#perspectiveFacade.perspective());
  readonly #perspectiveIncludesTotal = computed(() => this.#perspective()?.includeTotal);
  readonly #date = computed(() => this.#perspective()?.date || this.#appConfigFacade.initialDate());

  readonly isExecutive = input<boolean>(false);

  readonly includeTotal = input<boolean>();
  readonly #includeTotal = computed(() => {
    const inputIncludeTotal = this.includeTotal();
    if (inputIncludeTotal === true || inputIncludeTotal === false) {
      return inputIncludeTotal;
    }
    return this.#perspectiveIncludesTotal() || false;
  });

  constructor() {
    this.#showInsightsOnLoaded();
  }

  readonly #summary = resource<Insight[], {
    summarizationKey: string,
    period: string | undefined,
    date: Date | undefined,
    includeTotal: boolean,
    isExecutive: boolean,
  }>({
    request: () => ({
      summarizationKey: this.summarizationKey(),
      period: this.#optionsFacade.focusPeriod(),
      date: this.#date(),
      includeTotal: this.#includeTotal(),
      isExecutive: this.isExecutive(),
    }),
    loader: ({request, abortSignal}): Promise<Insight[]> => {
      if (!request.summarizationKey) {
        throw new Error('Summarization key is required');
      }
      const date = request.date ? DateTools.getDateString(request.date) : undefined;

      const params = request.isExecutive ?
        `includeTotal=true&isExecutive=true` :
        `period=${request.period}&includeTotal=${request.includeTotal}&date=${date}`;


      return fetch(
        `${this.#url}/summarize/${request.summarizationKey}?${params}`,
        {signal: abortSignal},
      )
        .then((response) => response.json() as Promise<{ summarize: Insight[] }>)
        .then((json) => json.summarize);
    },
  });

  readonly insights = this.#summary.value;
  protected readonly isLoading = this.#summary.isLoading;

  protected showInsights = signal(false);
  protected showDetails = signal(false);

  protected extendedState = computed(() => this.showDetails() ? 'extended' : 'collapsed');

  protected hasFailed = computed(() => this.#summary.error());

  toggleInsights(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isLoading() || !this.insights()?.length) {
      return;
    }

    if (this.showDetails()) {
      this.showDetails.set(false);
    }

    this.showInsights.update(x => !x);
  }

  toggleDetails(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.showDetails.update(x => !x);
  }

  swiperRef = viewChild.required<ElementRef<SwiperContainer>>('swiper');

  #showInsightsOnLoaded() {
    effect(() => {
      const insightsLoaded = this.#summary.status() === 4;
      if (insightsLoaded) {
        this.showInsights.set(true);
      }
    });
  }

  SvgSpriteIcon = SvgSpriteIcon;
}
